// LearnCore Frontend - 任务和奖励系统API客户端

import axios, { AxiosInstance } from 'axios';
import { appConfig } from '../config/env';
import type {
  TaskTemplate,
  UserTask,
  CreateTaskRequest,
  UpdateTaskRequest,
  CompleteTaskRequest,
  GetTasksResponse,
  GetDKPBalanceResponse,
  GetDKPTransactionsResponse,
  GetRewardsResponse,
  RedeemRewardRequest,
  GetRedemptionsResponse,
  ApiResponse,
  TaskStatus,
  TaskType,
  TaskCategory,
  TaskPriority
} from '../types/tasks-rewards';

class TasksApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: appConfig.apiBaseUrl,
      timeout: 30000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 请求拦截器
    this.client.interceptors.request.use(
      (config) => {
        console.log(`[TasksAPI] ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('[TasksAPI] Request error:', error);
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.client.interceptors.response.use(
      (response) => {
        console.log(`[TasksAPI] Response ${response.status}:`, response.data);
        return response;
      },
      (error) => {
        console.error('[TasksAPI] Response error:', error.response?.data || error.message);
        
        // 处理401错误 - 未授权
        if (error.response?.status === 401) {
          const currentPath = window.location.pathname;
          if (currentPath !== '/login' && currentPath !== '/register') {
            window.location.href = '/login';
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // 任务相关API
  async getTasks(params?: {
    status?: TaskStatus;
    type?: TaskType;
    category?: TaskCategory;
    due_date?: string;
    assigned_to?: string;
    page?: number;
    limit?: number;
  }): Promise<GetTasksResponse> {
    const response = await this.client.get<{ success: boolean; data: GetTasksResponse; error?: string }>('/tasks', {
      params
    });
    return response.data.data;
  }

  async createTask(request: CreateTaskRequest): Promise<{ task: UserTask }> {
    const response = await this.client.post<{ task: UserTask }>('/tasks', request);
    return response.data;
  }

  async updateTask(taskId: string, request: UpdateTaskRequest): Promise<{ task: UserTask }> {
    const response = await this.client.put<{ task: UserTask }>(`/tasks/${taskId}`, request);
    return response.data;
  }

  async completeTask(taskId: string, request?: CompleteTaskRequest): Promise<{ task: UserTask }> {
    const response = await this.client.post<{ task: UserTask }>(`/tasks/${taskId}/complete`, request || {});
    return response.data;
  }

  async deleteTask(taskId: string): Promise<ApiResponse> {
    const response = await this.client.delete<ApiResponse>(`/tasks/${taskId}`);
    return response.data;
  }

  async getTaskTemplates(params?: {
    type?: TaskType;
    category?: TaskCategory;
    include_user_created?: boolean;
  }): Promise<{ templates: TaskTemplate[] }> {
    const response = await this.client.get<{ templates: TaskTemplate[] }>('/templates', {
      params
    });
    return response.data;
  }

  async createTaskTemplate(request: {
    title: string;
    description?: string;
    type: TaskType;
    category: TaskCategory;
    target_value: number;
    dkp_reward: number;
    priority?: TaskPriority;
    is_system?: boolean;
  }): Promise<{ template: TaskTemplate }> {
    const response = await this.client.post<{ template: TaskTemplate }>('/templates', request);
    return response.data;
  }

  // DKP相关API
  async getDKPBalance(userId?: string): Promise<GetDKPBalanceResponse> {
    const response = await this.client.get<{ data: GetDKPBalanceResponse }>('/dkp/balance', {
      params: userId ? { user_id: userId } : {}
    });
    return response.data.data;
  }

  async getDKPTransactions(params?: {
    user_id?: string;
    type?: 'earn' | 'spend' | 'adjustment';
    start_date?: string;
    end_date?: string;
    page?: number;
    limit?: number;
  }): Promise<GetDKPTransactionsResponse> {
    const response = await this.client.get<{ data: GetDKPTransactionsResponse }>('/dkp/transactions', { params });
    return response.data.data;
  }

  async adjustDKP(request: {
    child_id: string;
    amount: number;
    reason: string;
  }): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>('/dkp/adjust', request);
    return response.data;
  }

  // 奖励相关API
  async getRewards(params?: {
    category?: string;
    min_dkp_cost?: number;
    max_dkp_cost?: number;
    available_only?: boolean;
    page?: number;
    limit?: number;
  }): Promise<GetRewardsResponse> {
    const response = await this.client.get<{ data: GetRewardsResponse }>('/rewards', { params });
    return response.data.data;
  }

  async createReward(request: {
    title: string;
    description?: string;
    type: 'digital' | 'privilege' | 'custom';
    dkp_cost: number;
    image_url?: string;
    validity_days?: number;
    max_redemptions_per_day?: number;
  }): Promise<{ reward: any }> {
    const response = await this.client.post<{ reward: any }>('/rewards', request);
    return response.data;
  }

  async updateReward(rewardId: string, request: {
    title?: string;
    description?: string;
    type?: 'digital' | 'privilege' | 'custom';
    dkp_cost?: number;
    image_url?: string;
    validity_days?: number;
    max_redemptions_per_day?: number;
    is_active?: boolean;
  }): Promise<{ reward: any }> {
    const response = await this.client.put<{ reward: any }>(`/rewards/${rewardId}`, request);
    return response.data;
  }

  async deleteReward(rewardId: string): Promise<ApiResponse> {
    const response = await this.client.delete<ApiResponse>(`/rewards/${rewardId}`);
    return response.data;
  }

  async getReward(rewardId: string): Promise<{ reward: any }> {
    const response = await this.client.get<{ reward: any }>(`/rewards/${rewardId}`);
    return response.data;
  }

  async getMyRewards(params?: {
    is_active?: boolean;
    type?: string[];
    page?: number;
    limit?: number;
  }): Promise<{ rewards: any[]; total: number; has_more: boolean }> {
    const response = await this.client.get<{ data: { rewards: any[]; total: number; has_more: boolean } }>('/rewards/my/list', { params });
    return response.data.data;
  }

  async redeemReward(request: RedeemRewardRequest): Promise<{ redemption: any }> {
    const response = await this.client.post<{ redemption: any }>('/rewards/redeem', request);
    return response.data;
  }

  async getRedemptions(params?: {
    user_id?: string;
    status?: 'pending' | 'approved' | 'rejected' | 'completed';
    start_date?: string;
    end_date?: string;
    page?: number;
    limit?: number;
  }): Promise<GetRedemptionsResponse> {
    const response = await this.client.get<GetRedemptionsResponse>('/rewards/redemptions', { params });
    return response.data;
  }

  async reviewRedemption(redemptionId: string, request: {
    status: 'approved' | 'rejected';
    parent_notes?: string;
  }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/rewards/redemptions/${redemptionId}/review`, request);
    return response.data;
  }

  // 统计数据相关API
  async getTaskStatistics(params?: {
    user_id?: string;
    period?: 'today' | 'week' | 'month';
  }): Promise<any> {
    const response = await this.client.get<any>('/statistics/tasks', { params });
    return response.data;
  }

  async getTrends(params?: {
    user_id?: string;
    days?: number;
  }): Promise<any> {
    const response = await this.client.get<any>('/statistics/trends', { params });
    return response.data;
  }

  async getAchievements(params?: {
    user_id?: string;
  }): Promise<any> {
    const response = await this.client.get<any>('/statistics/achievements', { params });
    return response.data;
  }

  // 进度更新相关API
  async recordLearningEvent(event: {
    event_type: string;
    event_data?: any;
    study_time_seconds?: number;
    accuracy_rate?: number;
    cards_count?: number;
    passage_id?: string;
    set_id?: string;
  }): Promise<any> {
    const response = await this.client.post<any>('/progress/events', event);
    return response.data;
  }

  async updateTaskProgress(taskId: string, progress: {
    progress_value: number;
    progress_type?: 'increment' | 'decrement' | 'set';
    notes?: string;
  }): Promise<any> {
    const response = await this.client.post<any>(`/progress/tasks/${taskId}`, progress);
    return response.data;
  }

  async getTaskProgress(taskId: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const response = await this.client.get<any>(`/progress/tasks/${taskId}/progress`, { params });
    return response.data;
  }

  async getLearningEvents(params?: {
    user_id?: string;
    days?: number;
  }): Promise<any> {
    const response = await this.client.get<any>('/progress/events', { params });
    return response.data;
  }
}

// 创建并导出单例实例
export const tasksApiClient = new TasksApiClient();

// 导出便捷的API对象（兼容现有代码）
export const tasksApi = {
  getTasks: (params?: any) => tasksApiClient.getTasks(params),
  createTask: (request: CreateTaskRequest) => tasksApiClient.createTask(request),
  updateTask: (taskId: string, request: UpdateTaskRequest) => tasksApiClient.updateTask(taskId, request),
  completeTask: (taskId: string, request?: CompleteTaskRequest) => tasksApiClient.completeTask(taskId, request),
  deleteTask: (taskId: string) => tasksApiClient.deleteTask(taskId),
  getTaskTemplates: (params?: any) => tasksApiClient.getTaskTemplates(params),
  createTaskTemplate: (request: any) => tasksApiClient.createTaskTemplate(request),
};

export const dkpApi = {
  getDKPBalance: (userId?: string) => tasksApiClient.getDKPBalance(userId),
  getDKPTransactions: (params?: any) => tasksApiClient.getDKPTransactions(params),
  adjustDKP: (request: any) => tasksApiClient.adjustDKP(request),
};

export const statisticsApi = {
  getTaskStatistics: (params?: { user_id?: string; period?: 'today' | 'week' | 'month' }) =>
    tasksApiClient.getTaskStatistics(params),
  getTrends: (params?: { user_id?: string; days?: number }) =>
    tasksApiClient.getTrends(params),
  getAchievements: (params?: { user_id?: string }) =>
    tasksApiClient.getAchievements(params),
};

export const progressApi = {
  recordLearningEvent: (event: any) => tasksApiClient.recordLearningEvent(event),
  updateTaskProgress: (taskId: string, progress: any) => tasksApiClient.updateTaskProgress(taskId, progress),
  getTaskProgress: (taskId: string, params?: any) => tasksApiClient.getTaskProgress(taskId, params),
  getLearningEvents: (params?: any) => tasksApiClient.getLearningEvents(params),
};

export const rewardsApi = {
  getRewards: (params?: any) => tasksApiClient.getRewards(params),
  createReward: (request: any) => tasksApiClient.createReward(request),
  updateReward: (rewardId: string, request: any) => tasksApiClient.updateReward(rewardId, request),
  deleteReward: (rewardId: string) => tasksApiClient.deleteReward(rewardId),
  getReward: (rewardId: string) => tasksApiClient.getReward(rewardId),
  getMyRewards: (params?: any) => tasksApiClient.getMyRewards(params),
  redeemReward: (request: RedeemRewardRequest) => tasksApiClient.redeemReward(request),
  getRedemptions: (params?: any) => tasksApiClient.getRedemptions(params),
  reviewRedemption: (redemptionId: string, request: any) => tasksApiClient.reviewRedemption(redemptionId, request),
};